import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ALL_SLOTS, MAX_PLAYERS_PER_SLOT, getFormulaById } from '../config/formulas';
import '../styles/WeeklyPlanning.css';

function WeeklyPlanning({ sessions, bookings, currentDate }) {
  const navigate = useNavigate();

  // Formater une date en YYYY-MM-DD
  const toDateStr = (d) => {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  // Calculer les jours de la semaine
  const weekDays = useMemo(() => {
    const start = new Date(currentDate);
    const day = start.getDay();
    start.setDate(start.getDate() - (day === 0 ? 6 : day - 1)); // Commencer lundi
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const dateStr = toDateStr(d);
      const isToday = dateStr === toDateStr(new Date());
      
      days.push({
        date: d,
        dateStr,
        isToday,
        label: d.toLocaleDateString('fr-FR', { weekday: 'short' }),
        dayNum: d.getDate(),
        monthLabel: d.toLocaleDateString('fr-FR', { month: 'short' })
      });
    }
    return days;
  }, [currentDate]);

  // Organiser les données par jour et créneau
  const grid = useMemo(() => {
    const data = {};
    weekDays.forEach(day => {
      data[day.dateStr] = {};
      ALL_SLOTS.forEach(slot => {
        const session = sessions.find(s => s.date === day.dateStr && s.time === slot);
        const slotBookings = bookings.filter(b => 
          b.date === day.dateStr && 
          b.slots && b.slots.includes(slot) && 
          b.status !== 'cancelled'
        );
        
        data[day.dateStr][slot] = {
          session,
          bookings: slotBookings,
          isOpen: session?.isOpen === true,
          isBlocked: session?.isBlocked === true,
          playerCount: session?.participantCount || 0,
          capacity: MAX_PLAYERS_PER_SLOT
        };
      });
    });
    return data;
  }, [weekDays, sessions, bookings]);

  // Navigation semaine
  const changeWeek = (delta) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (delta * 7));
    navigate(`/daily/${toDateStr(newDate)}`);
  };

  // Créneaux visibles (matin et après-midi séparés)
  const morningSlots = ALL_SLOTS.filter(s => parseInt(s) < 12);
  const afternoonSlots = ALL_SLOTS.filter(s => parseInt(s) >= 14);

  const renderSlotCell = (day, slot) => {
    const cell = grid[day.dateStr]?.[slot];
    if (!cell) return <td key={`${day.dateStr}-${slot}`} className="wp-cell empty"></td>;

    if (!cell.isOpen && !cell.isBlocked) {
      return <td key={`${day.dateStr}-${slot}`} className="wp-cell closed"></td>;
    }

    if (cell.isBlocked) {
      return (
        <td key={`${day.dateStr}-${slot}`} className="wp-cell blocked">
          <span className="wp-blocked-icon">🔒</span>
        </td>
      );
    }

    const fillPercent = Math.round((cell.playerCount / cell.capacity) * 100);
    const isFull = cell.playerCount >= cell.capacity;

    return (
      <td 
        key={`${day.dateStr}-${slot}`} 
        className={`wp-cell open ${isFull ? 'full' : ''} ${cell.bookings.length > 0 ? 'has-bookings' : ''}`}
        onClick={() => navigate(`/daily/${day.dateStr}`)}
        title={`${cell.playerCount}/${cell.capacity} joueurs`}
      >
        {cell.bookings.length > 0 ? (
          <div className="wp-bookings">
            {cell.bookings.map((b, i) => {
              const formula = getFormulaById(b.formulaId);
              return (
                <div key={i} className="wp-booking-chip" style={{ borderLeftColor: formula?.color || '#3b82f6' }}>
                  <span className="wp-chip-name">{b.contactName?.split(' ')[0]}</span>
                  <span className="wp-chip-count">{b.nbParticipants}j</span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="wp-empty-slot">
            <span className="wp-available">{cell.capacity - cell.playerCount}</span>
          </div>
        )}
        <div className={`wp-fill-bar`}>
          <div className="wp-fill-progress" style={{ width: `${fillPercent}%` }}></div>
        </div>
      </td>
    );
  };

  return (
    <div className="weekly-planning">
      {/* Navigation */}
      <div className="wp-header">
        <button onClick={() => changeWeek(-1)} className="wp-nav-btn">&lt; Sem. préc.</button>
        <h2>
          {weekDays[0].dayNum} {weekDays[0].monthLabel} — {weekDays[6].dayNum} {weekDays[6].monthLabel}
        </h2>
        <button onClick={() => changeWeek(1)} className="wp-nav-btn">Sem. suiv. &gt;</button>
      </div>

      {/* Grille */}
      <div className="wp-table-wrapper">
        <table className="wp-table">
          <thead>
            <tr>
              <th className="wp-time-header"></th>
              {weekDays.map(day => (
                <th 
                  key={day.dateStr} 
                  className={`wp-day-header ${day.isToday ? 'today' : ''}`}
                  onClick={() => navigate(`/daily/${day.dateStr}`)}
                >
                  <span className="wp-day-label">{day.label}</span>
                  <span className={`wp-day-num ${day.isToday ? 'today-num' : ''}`}>{day.dayNum}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Matin */}
            {morningSlots.map((slot, i) => (
              <tr key={slot}>
                <td className="wp-time-cell">
                  {i === 0 && <span className="wp-period-label">Matin</span>}
                  {slot}
                </td>
                {weekDays.map(day => renderSlotCell(day, slot))}
              </tr>
            ))}
            {/* Séparateur pause déjeuner */}
            <tr className="wp-lunch-break">
              <td colSpan={8}>🍽️ Pause déjeuner (12h-14h)</td>
            </tr>
            {/* Après-midi */}
            {afternoonSlots.map((slot, i) => (
              <tr key={slot}>
                <td className="wp-time-cell">
                  {i === 0 && <span className="wp-period-label">Après-midi</span>}
                  {slot}
                </td>
                {weekDays.map(day => renderSlotCell(day, slot))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Légende */}
      <div className="wp-legend">
        <span className="wp-legend-item"><span className="wp-legend-dot open"></span> Ouvert</span>
        <span className="wp-legend-item"><span className="wp-legend-dot has-bookings"></span> Réservé</span>
        <span className="wp-legend-item"><span className="wp-legend-dot full"></span> Complet</span>
        <span className="wp-legend-item"><span className="wp-legend-dot blocked"></span> Bloqué</span>
        <span className="wp-legend-item"><span className="wp-legend-dot closed"></span> Fermé</span>
      </div>
    </div>
  );
}

export default WeeklyPlanning;
